import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { isPlatformDirector } from "../../utils/roles";
import { PLANTILLA_SUGERIDA } from "../../data/welcomeEmailTemplates";
import "./ModificarMensajeAutomatico.css";
import DOMPurify from 'dompurify';

const LOGO_DEFECTO =
  "https://pub-a4634c2c47ff461eab7887f2d3fb9cbd.r2.dev/logo-academia.png";

const ModificarMensajeAutomatico = () => {
  const [rol, setRol] = useState("");
  const isDirector = isPlatformDirector(rol);

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && data?.usuario?.rol) {
          setRol(data.usuario.rol);
        } else {
          setRol("");
        }
      })
      .catch(() => {});
  }, []);
  
  const [mensaje, setMensaje] = useState("");
  const [correoEnvio, setCorreoEnvio] = useState("");
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarVistaPrevia, setMostrarVistaPrevia] = useState(false);
  const [logoPreview, setLogoPreview] = useState(LOGO_DEFECTO);
  const [logoSeleccionado, setLogoSeleccionado] = useState(null);

  // ------------------------------
  //   CARGA DE LOGO
  // ------------------------------
  useEffect(() => {
    axios
      .get(`${API_URL}/api/configuracion/logo-correo`, {
        withCredentials: true
      })
      .then((res) => {
        const url = res?.data?.logoCorreo;
        setLogoPreview(
          typeof url === "string" && url.startsWith("http")
            ? url
            : LOGO_DEFECTO
        );
      })
      .catch(() => setLogoPreview(LOGO_DEFECTO));
  }, []);

  // ------------------------------
  //   CARGA DE MENSAJE + CORREO
  // ------------------------------
  useEffect(() => {
    axios
      .get(`${API_URL}/api/configuracion/correo-envio`, {
        withCredentials: true
      })
      .then((res) => setCorreoEnvio(res.data.correoEnvio || ""));

    axios
      .get(`${API_URL}/api/configuracion/mensaje`, {
        withCredentials: true
      })
      .then((res) => {
        const msg = typeof res.data.mensaje === "string" ? res.data.mensaje : "";
        setMensaje(msg.trim() || PLANTILLA_SUGERIDA);
      });
  }, []);

  // ------------------------------
  //   SUBIR LOGO
  // ------------------------------
  const handleLogoSeleccionado = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoSeleccionado(file);

    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const subirLogoCorreo = () => {
    if (!logoSeleccionado) return alert("Selecciona una imagen primero.");

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const resUpload = await axios.post(
          `${API_URL}/subir-imagen-curso`,
          {
            nombre: logoSeleccionado.name,
            tipo: logoSeleccionado.type,
            contenidoBase64: reader.result
          },
          { withCredentials: true }
        );

        await axios.put(
          `${API_URL}/api/configuracion/logo-correo`,
          { logoCorreo: resUpload.data.url },
          { withCredentials: true }
        );

        setLogoPreview(resUpload.data.url);
        alert("Logo actualizado correctamente");
      } catch {
        alert("Error al subir el logo.");
      }
    };

    reader.readAsDataURL(logoSeleccionado);
  };

  // ------------------------------
  //   ACTUALIZAR CORREO
  // ------------------------------
  const actualizarCorreo = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoCorreo))
      return alert("Correo no válido.");

    axios
      .put(
        `${API_URL}/api/configuracion/correo-envio`,
        { correoEnvio: nuevoCorreo },
        { withCredentials: true }
      )
      .then(() => {
        setCorreoEnvio(nuevoCorreo);
        setMostrarModal(false);
        alert("Correo actualizado correctamente");
      });
  };

  // ------------------------------
  //   GUARDAR MENSAJE
  // ------------------------------
  const handleGuardar = () => {
    if (!isDirector) {
      return;
    }
    axios
      .put(
        `${API_URL}/api/configuracion/mensaje`,
        { mensaje },
        { withCredentials: true }
      )
      .then(() => alert("Mensaje actualizado correctamente"))
      .catch(() => alert("Error al actualizar el mensaje."));
  };

  // ------------------------------
  //   VISTA PREVIA
  // ------------------------------
  const previewConEjemplo = (html, logoUrl) => {
    const demo = {
      LOGO_URL: logoUrl,
      NOMBRE: "María",
      PRIMER_APELLIDO: "García",
      SEGUNDO_APELLIDO: "López",
      EMAIL: "maria@example.com",
      SETUP_URL: "https://tu-dominio/setup-account?token=ejemplo",
      CURSOS_LISTA: "Curso A, Curso B",
      ENLACE_LOGIN: "https://tu-dominio/login",
      FECHA: new Date().toLocaleDateString("es-ES")
    };

    return html.replace(/\{\{\s*([A-Z0-9_]+)\s*\}\}/g, (_, k) => demo[k] || "");
  };

  // ------------------------------
  //   RENDER (SOLO EL CONTENIDO)
  // ------------------------------
  return (
    <section className="cfgx-section">

      {/* Título */}
      <h2>Modificar Mensaje Automático</h2>

      {/* Correo de envío */}
      <div className="mc-email-contenedor">
        <div className="mc-email-label"><strong>Correo de envío actual:</strong></div>
        <div className="mc-email-linea">
          <div className="mc-email"><span>{correoEnvio}</span></div>

          <div className="botonesemail">
            <button
              className="mc-boton-horizontal"
              onClick={() => {
                setNuevoCorreo(correoEnvio);
                setMostrarModal(true);
              }}>
              Editar
            </button>

            <button
              className="mc-boton-horizontal"
              onClick={() => setMostrarVistaPrevia(!mostrarVistaPrevia)}>
              Vista previa
            </button>

            <button
              type="button"
              className="mc-boton-horizontal"
              disabled={!isDirector}
              onClick={handleGuardar}>
              Guardar Cambios
            </button>

            <button
              type="button"
              className="mc-boton-horizontal"
              disabled={!isDirector}
              onClick={() => {
                if (!isDirector) return;
                setMensaje(PLANTILLA_SUGERIDA);
              }}>
              Usar plantilla sugerida
            </button>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="selectorimagen">
        <label htmlFor="logoInput"><strong>Logo actual del correo:</strong></label>

        <div className="img">
          {logoPreview ? (
            <img src={logoPreview} alt="Vista previa del logo" />
          ) : (
            <p>No hay logo cargado.</p>
          )}
        </div>

        <input type="file" accept="image/*" id="logoInput" onChange={handleLogoSeleccionado} />

        <div className="botones-img">
          <button onClick={subirLogoCorreo} className="mc-boton-horizontal-img">
            Confirmar Logo
          </button>

          <button
            className="mc-boton-horizontal-img"
            onClick={() => {
              axios.put(
                `${API_URL}/api/configuracion/logo-correo`,
                { logoCorreo: LOGO_DEFECTO },
                { withCredentials: true }
              )
                .then(() => {
                  setLogoPreview(LOGO_DEFECTO);
                  alert("Logo restablecido al predeterminado.");
                })
                .catch(() => alert("Error al restablecer el logo."));
            }}>
            Restablecer Logo por Defecto
          </button>
        </div>
      </div>

      {/* Modal cambio de correo */}
      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Cambiar Correo de Envío</h3>
            <input type="email" value={nuevoCorreo} onChange={(e) => setNuevoCorreo(e.target.value)} />
            <button onClick={actualizarCorreo}>Confirmar</button>
            <button onClick={() => setMostrarModal(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Mensaje automático */}
      {isDirector ? (
        <textarea
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          className="mc-textarea"
          rows="10"
          placeholder="Escribe aquí el nuevo mensaje automático..."
        />
      ) : (
        <p className="mc-mensaje-fijo">{mensaje}</p>
      )}

      {/* Vista previa */}
      {mostrarVistaPrevia && (
        <div className="mc-vista-previa">
          <h3>Vista previa del mensaje:</h3>
          <div
            className="mc-vista-contenido"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewConEjemplo(mensaje, logoPreview)) }}
          />
        </div>
      )}
    </section>
  );
};

export default ModificarMensajeAutomatico;
